import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth/useAuth";
import { useRole } from "../lib/auth/useRole";
import { ScreenHeader } from "../components/ScreenHeader";
import { YesterdayCard } from "../components/YesterdayCard";
import { Icons } from "../components/Icons";
import { useWorkerTimesheets } from "../hooks/useTimesheet";
import { useTenantSettings } from "../hooks/useTenantSettings";
import { getWorkerUrl } from "../lib/session";
import { localDayKey, previousWorkday } from "../lib/date";
import { formatDateFull, relativeDayLabel } from "../lib/format";
import { TIMESHEET_STATUS_HINT, TIMESHEET_STATUS_TAG } from "../lib/statusMeta";
import { currentPeriod } from "../lib/weekBoard";
import type { TimesheetPeriodType } from "../api/types";

const WEEKDAYS_FR = [
  "dimanche",
  "lundi",
  "mardi",
  "mercredi",
  "jeudi",
  "vendredi",
  "samedi",
];

/** « hier », ou le nom du dernier jour ouvré quand ce n'était pas hier. */
function dayName(day: Date): string {
  return relativeDayLabel(day) === "hier"
    ? "hier"
    : (WEEKDAYS_FR[day.getDay()] ?? "");
}

/** La veille, seule : ses heures, ses créneaux à imputer sur place, puis l'état
 * de la feuille de la période. Le temps sans projet de la semaine entière se
 * relit dans la feuille, pas ici : deux périmètres sur un écran donnaient deux
 * chiffres sans dire lequel était lequel. */
export function HierScreen() {
  const { user } = useAuth();
  const role = useRole();
  const manages = role === "manager" || role === "admin";
  const workerUrl = (() => {
    try {
      return getWorkerUrl(user);
    } catch {
      return "";
    }
  })();

  const dayDate = previousWorkday(new Date());
  const day = localDayKey(dayDate);
  const name = dayName(dayDate);
  const title = name === "hier" ? "Ma journée d'hier" : `Ma journée de ${name}`;

  const { data: settings } = useTenantSettings();
  const periodType: TimesheetPeriodType =
    settings?.timesheet_period_type ?? "weekly";
  const period = currentPeriod(periodType);
  const { data: sheets } = useWorkerTimesheets(workerUrl);
  const currentSheet = sheets?.timesheets.find(
    (t) =>
      t.period_start.slice(0, 10) <= period.end &&
      t.period_end.slice(0, 10) >= period.start,
  );

  // Réimputer exige que la feuille du jour, s'il y en a une, soit en brouillon.
  const daySheet = sheets?.timesheets.find(
    (t) =>
      t.period_start.slice(0, 10) <= day && t.period_end.slice(0, 10) >= day,
  );
  const dayEditable = !!sheets && (!daySheet || daySheet.status === "draft");

  const unit = periodType === "monthly" ? "du mois" : "de la semaine";

  return (
    <>
      <ScreenHeader title={title} sub={formatDateFull(dayDate)} />
      <YesterdayCard
        workerUrl={workerUrl}
        day={day}
        label={title}
        editable={dayEditable}
      />

      <h2 className="section-label">Ensuite</h2>
      <Link className="recap-link next-sheet" to="/timesheet">
        <span className="ic">
          <Icons.cal size={17} sw={1.8} />
        </span>
        <span className="text">
          <span className="lbl">
            Feuille {unit}
            {sheets && (
              <span
                className={`tag ${currentSheet ? TIMESHEET_STATUS_TAG[currentSheet.status].cls : "tag--brand"}`}
              >
                {currentSheet
                  ? TIMESHEET_STATUS_TAG[currentSheet.status].label
                  : "Pas encore créée"}
              </span>
            )}
          </span>
          <span className="sub">
            {currentSheet
              ? TIMESHEET_STATUS_HINT[currentSheet.status]
              : `Relisez la période et soumettez-la à la fin ${unit}`}
          </span>
        </span>
        <Icons.chev size={14} />
      </Link>

      {manages && (
        <Link className="recap-link" to="/recap">
          <span className="ic">
            <Icons.team size={17} sw={1.8} />
          </span>
          <span className="text">
            <span className="lbl">Récap de la veille</span>
            <span className="sub">
              Ce que l'équipe a pointé, et ce qui manque
            </span>
          </span>
          <Icons.chev size={14} />
        </Link>
      )}
    </>
  );
}

export default HierScreen;
